import apiClient from "../clients/apiClient";
import { ApiResponse } from "../schemas/apiResponseSchema";

export interface CheckUserSignOnResponse {
    signon: 0 | 1;
    signondate?: string | null;
    station_id?: string | number | null;
    shiftno?: number | null;
}

const toSignOnFlag = (value: unknown): 0 | 1 => {
    if (typeof value === "number") {
        return value === 1 ? 1 : 0;
    }
    if (typeof value === "boolean") {
        return value ? 1 : 0;
    }
    if (typeof value === "string") {
        const normalized = value.toLowerCase().trim();
        return normalized === "1" || normalized === "true" || normalized === "ture" ? 1 : 0;
    }
    return 0;
};

const pickFirst = (source: any, keys: string[]) => {
    for (const key of keys) {
        if (source && source[key] !== undefined && source[key] !== null) {
            return source[key];
        }
    }
    return null;
};

export const checkUserSignOnStatus = async (userName?: string): Promise<CheckUserSignOnResponse> => {
    try {
        const params = userName ? { params: { user_name: userName } } : {};
        const response: ApiResponse<any> = (await apiClient.get("/signon/check_status", params)).data;
        const payload = response?.data ?? {};

        const shiftVal = pickFirst(payload, ["shiftno", "shift_no", "shift"]);
        return {
            signon: toSignOnFlag(pickFirst(payload, ["signon", "sign_on", "is_signed_on", "signed_in", "is_signon", "isSignOn"])),
            signondate: pickFirst(payload, ["signondate", "database_date", "db_date", "sign_on_date", "date"]),
            station_id: pickFirst(payload, ["station_id", "stationid", "stationId"]),
            shiftno: shiftVal != null ? Number(shiftVal) : null,
        };
    } catch (error: any) {
        const detail =
            error.response?.data?.detail ||
            error.response?.data?.message ||
            error.message;
        throw new Error(detail || "Failed to check user sign on status");
    }
};

export const checkUserSignOn = checkUserSignOnStatus;

export interface UserSignOnRecord {
    signon: number | null;
    signondate: string | null;
    signontime?: string | null;
    stationid: string | number | null;
    shiftno?: number | null;
    floatamt?: number | null;
    /** pos_shift_log id of the shift closed by a sign-off */
    shift_log_id?: number | null;
}

/** Physical total the cashier counted for one payment mode at sign-off. */
export interface SignOffDeclaration {
    pay_code: string;
    amount: number;
    /** EDC refunds - card modes only */
    refund_amount?: number | null;
}

export const signOffCurrentUser = async (
    userName: string,
    declarations: SignOffDeclaration[]
): Promise<UserSignOnRecord | null> => {
    try {
        const response: ApiResponse<any> = (
            await apiClient.put("/signon/signoff", { user_name: userName, declarations })
        ).data;
        const data = response?.data ?? null;
        if (!data) return null;
        return {
            signon: data.signon ?? null,
            signondate: data.signondate ?? null,
            signontime: data.signontime ?? null,
            stationid: data.stationid ?? null,
            shiftno: data.shiftno != null ? Number(data.shiftno) : null,
            floatamt: data.floatamt ?? null,
            shift_log_id: data.shift_log_id != null ? Number(data.shift_log_id) : null,
        };
    } catch (error: any) {
        const detail =
            error.response?.data?.detail ||
            error.response?.data?.message ||
            error.message;
        throw new Error(detail || "Failed to sign off user");
    }
};

interface UpdateUserSignOnPayload {
    user_name: string;
    signon?: 0 | 1;
    signondate?: string;
    signontime?: string;
    station_id?: string;
    floatamt?: number;
}

const cleanParams = (params: Record<string, any>) => {
    const cleaned: Record<string, any> = {};
    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null) continue;
        if (typeof value === "string" && value.trim() === "") continue;
        cleaned[key] = value;
    }
    return cleaned;
};

export const getUserSignOnRecords = async (userName: string): Promise<UserSignOnRecord | null> => {
    try {
        const params: Record<string, string | number> = { user_name: userName };
        const response: ApiResponse<any> = (
            await apiClient.get("/signon/user_records", { params })
        ).data;
        const data = response?.data ?? null;
        if (!data) return null;
        return {
            signon: data.signon ?? null,
            signondate: data.signondate ?? null,
            signontime: data.signontime ?? null,
            stationid: data.stationid ?? null,
            shiftno: data.shiftno != null ? Number(data.shiftno) : null,
            floatamt: data.floatamt ?? null,
        };
    } catch (error: any) {
        const detail =
            error.response?.data?.detail ||
            error.response?.data?.message ||
            error.message;
        throw new Error(detail || "Failed to retrieve sign-on records");
    }
};

export const updateUserSignOnRecords = async (payload: UpdateUserSignOnPayload): Promise<UserSignOnRecord | null> => {
    try {
        const params = cleanParams(payload as any);
        const response: ApiResponse<any> = (
            await apiClient.put("/signon/user_records", null, { params })
        ).data;
        const data = response?.data ?? null;
        if (!data) return null;
        return {
            signon: data.signon ?? null,
            signondate: data.signondate ?? null,
            signontime: data.signontime ?? null,
            stationid: data.stationid ?? null,
            shiftno: data.shiftno != null ? Number(data.shiftno) : null,
            floatamt: data.floatamt ?? null,
        };
    } catch (error: any) {
        const detail =
            error.response?.data?.detail ||
            error.response?.data?.message ||
            error.message;
        throw new Error(detail || "Failed to update sign-on records");
    }
};

export interface StationOption {
    id: number;
    station_id: string;
    station_loc: string | null;
    is_occupied: boolean;
    occupied_by: string | null;
    is_assigned_elsewhere: boolean;
    next_shift?: number;
}

export interface StationListResult {
    stations: StationOption[];
    message: string;
}

export const getStations = async (terminalId?: string | null): Promise<StationListResult> => {
    try {
        const params = terminalId ? { terminal_id: terminalId } : {};
        const response: ApiResponse<any[]> = (
            await apiClient.get("/signon/stations", { params })
        ).data;
        const stations = (response?.data ?? []).map((row: any) => ({
            id: row.id,
            station_id: row.station_id,
            station_loc: row.station_loc ?? null,
            is_occupied: Boolean(row.is_occupied),
            occupied_by: row.occupied_by ?? null,
            is_assigned_elsewhere: Boolean(row.is_assigned_elsewhere),
            next_shift: row.next_shift != null ? Number(row.next_shift) : 1,
        }));
        return { stations, message: response?.message ?? "" };
    } catch (error: any) {
        const detail =
            error.response?.data?.detail ||
            error.response?.data?.message ||
            error.message;
        throw new Error(detail || "Failed to retrieve stations");
    }
};

export const claimStation = async (stationPk: number, terminalId: string): Promise<void> => {
    try {
        await apiClient.post(`/signon/stations/${stationPk}/claim`, { terminal_id: terminalId });
    } catch (error: any) {
        const detail =
            error.response?.data?.detail ||
            error.response?.data?.message ||
            error.message;
        throw new Error(detail || "Failed to claim station");
    }
};

/** A payment mode the cashier counts at sign-off; card modes also take EDC refunds. */
export interface SignOffPaymode {
    pay_code: string;
    description: string;
    category: "cash" | "card" | "other" | "credit";
    has_refund: boolean;
}

export const getSignOffPaymodes = async (): Promise<SignOffPaymode[]> => {
    try {
        const response: ApiResponse<SignOffPaymode[]> = (await apiClient.get("/signon/signoff_paymodes")).data;
        return response?.data ?? [];
    } catch (error: any) {
        const detail =
            error.response?.data?.detail ||
            error.response?.data?.message ||
            error.message;
        throw new Error(detail || "Failed to load payment modes");
    }
};

export interface CashVarianceReport {
    shift: {
        id: number;
        business_date: string | null;
        station_id: string;
        station_loc: string | null;
        branch_name: string | null;
        user_name: string;
        shift_no: number | null;
        status: string;
        signon_time: string | null;
        signoff_time: string | null;
    };
    company: { name: string; address: string };
    invoice_summary: {
        sales_count: number;
        sales_total: number;
        return_count: number;
        return_total: number;
        cancelled_count: number;
        cancelled_total: number;
        first_invoice: string | null;
        last_invoice: string | null;
    };
    /** Refunds and cash out are negative; counted / variance are null when the shift was not declared. */
    cash: {
        opening: number;
        sales: number;
        refunds: number;
        cash_in: number;
        cash_out: number;
        expected: number;
        counted: number | null;
        variance: number | null;
    };
    card: {
        sales: number;
        refunds: number;
        total: number;
        sales_counted: number | null;
        refunds_counted: number | null;
        total_counted: number | null;
        variance: number | null;
    };
    methods: { pay_code: string; label: string; system: number; actual: number | null; variance: number | null }[];
    methods_total: { system: number; actual: number | null; variance: number | null };
    info_lines: { label: string; amount: number }[];
    is_declared: boolean;
    generated_at: string;
}

/** Daily Cash Reconciliation for a closed shift; without shiftId, the user's latest closed shift. */
export const getCashVarianceReport = async (shiftId?: number | null): Promise<CashVarianceReport> => {
    try {
        const params = shiftId != null ? { shift_id: shiftId } : {};
        const response: ApiResponse<CashVarianceReport> = (
            await apiClient.get("/signon/cash_variance_report", { params })
        ).data;
        if (!response?.data) {
            throw new Error(response?.message || "Cash variance report not found");
        }
        return response.data;
    } catch (error: any) {
        const detail =
            error.response?.data?.detail ||
            error.response?.data?.message ||
            error.message;
        throw new Error(detail || "Failed to load cash variance report");
    }
};

export interface ServerDateTime {
    date: string;
    time: string;
    datetime: string;
}

export const getServerDateTime = async (): Promise<ServerDateTime> => {
    try {
        const response: ApiResponse<any> = (
            await apiClient.get("/signon/server_datetime")
        ).data;
        const data = response?.data ?? {};
        return {
            date: data.date ?? "",
            time: data.time ?? "",
            datetime: data.datetime ?? "",
        };
    } catch (error: any) {
        // Fallback to client-side datetime if server call fails
        const now = new Date();
        const yyyy = String(now.getFullYear());
        const mm = String(now.getMonth() + 1).padStart(2, "0");
        const dd = String(now.getDate()).padStart(2, "0");
        const hh = String(now.getHours()).padStart(2, "0");
        const min = String(now.getMinutes()).padStart(2, "0");
        const ss = String(now.getSeconds()).padStart(2, "0");
        return {
            date: `${yyyy}-${mm}-${dd}`,
            time: `${hh}:${min}:${ss}`,
            datetime: `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`,
        };
    }
};
